import { ref } from "vue";
import { httpRequest } from "../http";

const POLL_INTERVAL_MS = 7000;

const jsonHeaders = { Accept: "application/json" };

/**
 * Background report export (PDF or XLSX): POST `url` then poll `status_url`.
 */
export const useExportPoller = ({ getUrls, errorLabel, closeDropdown }) => {
  const isGenerating = ref(false);
  const pollTimer = ref(null);
  const readyUrl = ref(null);
  const errorMessage = ref(null);

  const stopPolling = () => {
    if (pollTimer.value) {
      clearInterval(pollTimer.value);
      pollTimer.value = null;
    }
  };

  const startPolling = () => {
    if (pollTimer.value) {
      return;
    }

    const statusUrl = getUrls()?.status_url;
    if (!statusUrl) {
      isGenerating.value = false;
      return;
    }

    pollTimer.value = setInterval(() => {
      httpRequest(statusUrl, { method: "GET", headers: jsonHeaders })
        .then((response) => response.json())
        .then(handleResponse)
        .catch(() => {
          stopPolling();
          isGenerating.value = false;
        });
    }, POLL_INTERVAL_MS);
  };

  const handleResponse = (data, options = {}) => {
    const { autoDownloadWhenReady = true } = options;

    if (data.status === "completed" && data.report_url) {
      stopPolling();
      isGenerating.value = false;
      readyUrl.value = data.report_url;
      errorMessage.value = null;

      if (autoDownloadWhenReady) {
        closeDropdown();
        window.location.href = readyUrl.value;
      }

      return;
    }

    if (
      data.status === "generating" ||
      data.status === "waiting_for_calculation" ||
      data.status === "calculation_not_ready"
    ) {
      isGenerating.value = true;
      errorMessage.value = null;
      startPolling();
      return;
    }

    if (data.status === "not_started") {
      stopPolling();
      isGenerating.value = false;
      return;
    }

    if (data.status === "errored") {
      stopPolling();
      isGenerating.value = false;
      readyUrl.value = null;
      errorMessage.value = data.message || errorLabel;
    }
  };

  const checkStatusOnLoad = () => {
    const statusUrl = getUrls()?.status_url;
    if (!statusUrl) {
      return;
    }

    httpRequest(statusUrl, { method: "GET", headers: jsonHeaders })
      .then((response) => response.json())
      .then((data) => handleResponse(data, { autoDownloadWhenReady: false }))
      .catch(() => {
        stopPolling();
        isGenerating.value = false;
      });
  };

  const requestExport = () => {
    if (readyUrl.value) {
      closeDropdown();
      window.location.href = readyUrl.value;
      return;
    }

    if (isGenerating.value) {
      return;
    }

    isGenerating.value = true;
    errorMessage.value = null;
    const generateUrl = getUrls()?.url;

    httpRequest(generateUrl, { method: "POST", headers: jsonHeaders })
      .then((response) => response.json())
      .then((data) => handleResponse(data, { autoDownloadWhenReady: true }))
      .catch(() => {
        stopPolling();
        isGenerating.value = false;
        errorMessage.value = errorLabel;
      });
  };

  return {
    isGenerating,
    errorMessage,
    stopPolling,
    checkStatusOnLoad,
    requestExport,
  };
};
